/**
 * Pending-move continuity patches.
 *
 * WHY THIS EXISTS (see the old calendar's `awaitingFlushRef` in Calendar.tsx):
 * committing a move calls the reschedule mutation, whose OPTIMISTIC cache
 * patch is applied in `onMutate` — asynchronously (it awaits `cancelQueries`
 * before writing). Between "drag released / move placed" and "optimistic
 * patch re-derived into `calendarEvents`" the authoritative snapshot still
 * holds the PRE-move position; rendering it would flash every drop back to
 * its old slot for a frame or two.
 *
 * Instead of freezing the whole events array (the old calendar's fix — which
 * created the very abort/recovery problems this engine is designed around),
 * we patch ONLY the moved events on top of the incoming snapshot: a small map
 * keyed by the STABLE slot id, applied to the raw `CalendarEvent[]` before
 * the index is built. Everything else in the snapshot (other users' changes,
 * socket flushes, refetches) flows through untouched, at any time.
 *
 * Retirement (per entry):
 *  - the authoritative snapshot matches the target        → caught up, drop it
 *  - the commit promise settles (incl. error → rollback)  → server answered,
 *    the cache is canonical either way (reconciled or restored) — drop it so
 *    a rollback actually snaps back
 *  - safety timeout                                       → never patch forever
 *
 * Entries carry a monotonically increasing `seq`: re-dragging the same slot
 * while the previous commit is still in flight REPLACES its entry, and the
 * stale batch's settle/timeout can no longer retire the newer one
 * (last-issued-wins, mirroring the mutation layer's sequence guards).
 */
import type { CalendarEvent } from "../../Calendar/controller/adapters/types";
import type { MCEventMove } from "./types";

export interface MCPendingMove {
  /** Stable identity: extendedProps.timeSlotId ?? String(raw.id) */
  key: string;
  seq: number;
  targetResourceId: string;
  targetStartMs: number;
  targetEndMs: number;
}

let pendingSeq = 0;

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** Stable slot identity of a raw calendar event (slot ids survive SPLIT/MERGE). */
export const pendingKeyForEvent = (raw: CalendarEvent): string | null => {
  const key = raw.extendedProps?.timeSlotId ?? (raw.id != null ? String(raw.id) : "");
  return key || null;
};

/**
 * Resolve a move's absolute target times from the raw slot: keep the slot's
 * own DATE (continuous slots live on their work-plan-week date), apply the
 * target time-of-day, preserve the raw duration (day-range clamping in the
 * grid never distorts it). Shared with the mutation bridge so the visual
 * patch and the dispatched mutation can never disagree.
 */
export const resolveMoveTimes = (
  raw: CalendarEvent,
  targetStartMin: number,
): { start: Date; end: Date } | null => {
  const rawStart = toDate(raw.start);
  const rawEnd = toDate(raw.end);
  if (!rawStart || !rawEnd) return null;
  const start = new Date(rawStart);
  start.setHours(Math.floor(targetStartMin / 60), targetStartMin % 60, 0, 0);
  const end = new Date(start.getTime() + (rawEnd.getTime() - rawStart.getTime()));
  return { start, end };
};

/** Build patch entries for a committed batch (unresolvable moves skipped). */
export const buildPendingMoves = (moves: ReadonlyArray<MCEventMove>): MCPendingMove[] => {
  const entries: MCPendingMove[] = [];
  for (const move of moves) {
    const raw = move.event.raw;
    const key = pendingKeyForEvent(raw);
    const times = resolveMoveTimes(raw, move.targetStartMin);
    if (!key || !times) continue;
    entries.push({
      key,
      seq: ++pendingSeq,
      targetResourceId: move.targetResourceId,
      targetStartMs: times.start.getTime(),
      targetEndMs: times.end.getTime(),
    });
  }
  return entries;
};

/** True when the authoritative raw event already sits at the pending target. */
export const isPendingSatisfied = (raw: CalendarEvent, pending: MCPendingMove): boolean => {
  const start = toDate(raw.start);
  const end = toDate(raw.end);
  if (!start || !end) return false;
  const resourceId =
    (raw as CalendarEvent & { resourceId?: string; resourceIds?: string[] }).resourceId ??
    (raw as CalendarEvent & { resourceIds?: string[] }).resourceIds?.[0];
  return (
    resourceId === pending.targetResourceId &&
    start.getTime() === pending.targetStartMs &&
    end.getTime() === pending.targetEndMs
  );
};

/**
 * Apply pending patches to a raw snapshot. Untouched events keep their object
 * identity (memoized EventBlocks stay still); with no pending entries the
 * input array itself is returned (index memo bails out entirely).
 */
export const applyPendingMoves = (
  events: CalendarEvent[],
  pending: ReadonlyMap<string, MCPendingMove>,
): CalendarEvent[] => {
  if (pending.size === 0) return events;
  let changed = false;
  const patched = events.map((raw) => {
    const key = pendingKeyForEvent(raw);
    if (!key) return raw;
    const entry = pending.get(key);
    if (!entry || isPendingSatisfied(raw, entry)) return raw;
    changed = true;
    return {
      ...raw,
      start: new Date(entry.targetStartMs),
      end: new Date(entry.targetEndMs),
      resourceId: entry.targetResourceId,
    } as CalendarEvent;
  });
  return changed ? patched : events;
};

/** Entries already satisfied by the snapshot (ready to retire). */
export const satisfiedPendingKeys = (
  events: CalendarEvent[],
  pending: ReadonlyMap<string, MCPendingMove>,
): string[] => {
  if (pending.size === 0) return [];
  const satisfied: string[] = [];
  for (const raw of events) {
    const key = pendingKeyForEvent(raw);
    if (!key) continue;
    const entry = pending.get(key);
    if (entry && isPendingSatisfied(raw, entry)) satisfied.push(key);
  }
  return satisfied;
};
